import { useEffect } from 'react';
import { ParallaxProvider } from 'react-scroll-parallax';

import { LandingPageV2Props } from 'cms';
import homePageV2EntryLookup from 'cms/entries/homePageV2';
import landingPageV2EntryLookup from 'cms/entries/landingPageV2';
import RichTextRenderer from 'cms/renderer';
import { CacheSafeGetServerSideProps } from 'declarations/shared/serverSideProps';
import { useObservability } from 'helpers/observability';
import { ReturnExperienceContextProvider } from 'helpers/ReturnExperienceContext';
import { StickyCTABarContextProvider } from 'helpers/StickyCTABarContext';
import { useRouter } from 'next/router';
import LandingPageV2Layout, {
  getLandingpageV2ServerSideProps,
} from 'pages/shared/LandingPageV2Layout';
import { setContentPageFontPreloadLinkHeader } from 'server/earlyHints';

export const getServerSideProps: CacheSafeGetServerSideProps<LandingPageV2Props> = async (
  context,
) => {
  const result = await getLandingpageV2ServerSideProps(context);

  if ('props' in result) {
    const props = await result.props;
    setContentPageFontPreloadLinkHeader(context.res);
    return {
      ...result,
      props,
    };
  }

  return result;
};

const LandingPageWithContext = (props: LandingPageV2Props) => {
  const { trackJourneyEvent } = useObservability();
  const router = useRouter();
  const nextJsPath = router.asPath.split('?')[0];
  const isHomePage = nextJsPath === '/'; // || true to debug HP perf
  const cmsEntryLookup = isHomePage ? homePageV2EntryLookup : landingPageV2EntryLookup;

  useEffect(() => {
    // only measuring homepage load-time performance
    if (typeof window === 'undefined') return;
    // only measuring if the slug contains 'homepage'
    // we have a few variants of the homepage like /homepage-syw-v2-home-guide
    if (window.location.pathname !== '/') return;

    trackJourneyEvent('platform', 'pageLoadedEvent', {
      // Next.js route, which is not necessarily the client-side URL
      routerPath: nextJsPath,
      loadTimeMs: Math.round(performance.now()),
    });
  }, []);

  return (
    <ParallaxProvider>
      <ReturnExperienceContextProvider
        showReturnExperience={props.fields.showReturnExperienceBanner}
      >
        <StickyCTABarContextProvider>
          <LandingPageV2Layout {...props}>
            <RichTextRenderer.Component
              body={props.fields.body}
              idToLoaderData={props.idToLoaderData}
              entryLookup={cmsEntryLookup}
            />
          </LandingPageV2Layout>
        </StickyCTABarContextProvider>
      </ReturnExperienceContextProvider>
    </ParallaxProvider>
  );
};

export default LandingPageWithContext;
